// Sablier en SVG natif : ascendance (2 générations) au-dessus de la personne
// racine, descendance (2 générations) en dessous. Ancêtres colorés par
// génération, descendants en teinte neutre, racine mise en avant au centre.

import React, { forwardRef } from 'react';
import { Membre, Relation } from '../../types';
import { getAncestors, getDescendants, AncestorNode, DescendantNode } from '../../utils/lineage';
import { StylePreset, resolveBg, PersonBox } from './chartCommon';

export interface SablierChartProps {
  rootId: number;
  membres: Membre[];
  relations: Relation[];
  style: StylePreset;
  isDark: boolean;
}

const BOX_W = 128, BOX_H = 44, COL_GAP = 14, ROW_GAP = 40, PAD = 18;
const GENS = 2;

function collectAnc(node: AncestorNode | null, out: AncestorNode[]) {
  if (!node) return;
  out.push(node);
  collectAnc(node.pere, out);
  collectAnc(node.mere, out);
}

const SablierChart = forwardRef<SVGSVGElement, SablierChartProps>(function SablierChart(
  { rootId, membres, relations, style, isDark }, ref,
) {
  const rootMembre = membres.find(m => m.id === rootId);
  const anc = getAncestors(rootId, membres, relations, GENS);
  const desc = getDescendants(rootId, membres, relations, GENS);
  const colStep = BOX_W + COL_GAP;

  if (!rootMembre) {
    return <svg ref={ref} width={1} height={1} xmlns="http://www.w3.org/2000/svg" />;
  }

  // ── Ascendance : colonnes (racine centrée sous ses parents) ──
  const ancNodes: AncestorNode[] = [];
  collectAnc(anc, ancNodes);
  const ancColOf = new Map<AncestorNode, number>();
  let nextCol = 0;
  (function layout(n: AncestorNode | null): number {
    if (!n) return 0;
    if (n.gen >= GENS || (!n.pere && !n.mere)) { const c = nextCol++; ancColOf.set(n, c); return c; }
    const cs: number[] = [];
    if (n.pere) cs.push(layout(n.pere));
    if (n.mere) cs.push(layout(n.mere));
    const c = cs.reduce((a, b) => a + b, 0) / cs.length;
    ancColOf.set(n, c);
    return c;
  })(anc);
  const ancMaxCol = ancNodes.length ? Math.max(...ancNodes.map(n => ancColOf.get(n) ?? 0)) : 0;

  // ── Descendance : lignes par génération ──
  const descRows: DescendantNode[][] = [[], [], []];
  (function walk(n: DescendantNode | null) {
    if (!n) return;
    if (descRows[n.gen]) descRows[n.gen].push(n);
    n.children.forEach(walk);
  })(desc);
  const leaves = descRows[2].length || descRows[1].length || 1;

  const totalCols = Math.max(ancMaxCol, leaves - 1, 1);
  const W = totalCols * colStep + BOX_W + PAD * 2;
  const midX = W / 2;
  const ancTop = PAD;
  const rootY = ancTop + GENS * (BOX_H + ROW_GAP);
  const H = rootY + BOX_H + GENS * (BOX_H + ROW_GAP) + PAD;

  const ancX = (n: AncestorNode) => PAD + ((ancColOf.get(n) ?? 0) + (totalCols - ancMaxCol) / 2) * colStep;
  const ancY = (n: AncestorNode) => ancTop + (GENS - n.gen) * (BOX_H + ROW_GAP);

  const edges: React.ReactNode[] = [];
  const boxes: React.ReactNode[] = [];

  // Connecteurs ascendance (racine → parents → grands-parents)
  ancNodes.forEach((n, i) => {
    [n.pere, n.mere].forEach((p, pi) => {
      if (!p) return;
      const childX = (n.gen === 0 ? midX - BOX_W / 2 : ancX(n)) + BOX_W / 2;
      const childY = n.gen === 0 ? rootY : ancY(n);
      const parentX = ancX(p) + BOX_W / 2, parentY = ancY(p) + BOX_H;
      const midY = (childY + parentY) / 2;
      edges.push(
        <path key={`ae${i}-${pi}`}
          d={`M ${childX} ${childY} L ${childX} ${midY} L ${parentX} ${midY} L ${parentX} ${parentY}`}
          fill="none" stroke={style.edge} strokeWidth={style.edgeW} strokeLinecap="round" />,
      );
    });
  });

  // Positions descendance (feuilles centrées, parents au barycentre)
  const dxOf = new Map<DescendantNode, number>();
  let li = 0;
  const leafX = (i: number) => PAD + i * colStep + (W - 2 * PAD - (leaves - 1) * colStep - BOX_W) / 2;
  (function assignX(n: DescendantNode | null) {
    if (!n) return;
    if (n.gen === GENS || n.children.length === 0) {
      dxOf.set(n, n.gen === 0 ? midX - BOX_W / 2 : leafX(li++));
      return;
    }
    n.children.forEach(assignX);
    if (n.gen === 0) dxOf.set(n, midX - BOX_W / 2);
    else {
      const xs = n.children.map(ch => dxOf.get(ch) ?? 0);
      dxOf.set(n, xs.reduce((a, b) => a + b, 0) / xs.length);
    }
  })(desc);
  const dyOf = (n: DescendantNode) => rootY + n.gen * (BOX_H + ROW_GAP);

  // Connecteurs descendance
  let de = 0;
  (function walk(n: DescendantNode | null) {
    if (!n) return;
    n.children.forEach(ch => {
      const cx = (dxOf.get(n) ?? 0) + BOX_W / 2, cy = dyOf(n) + BOX_H;
      const kx = (dxOf.get(ch) ?? 0) + BOX_W / 2, ky = dyOf(ch);
      const midY = (cy + ky) / 2;
      edges.push(
        <path key={`de${de++}`}
          d={`M ${cx} ${cy} L ${cx} ${midY} L ${kx} ${midY} L ${kx} ${ky}`}
          fill="none" stroke={style.edge} strokeWidth={style.edgeW} strokeLinecap="round" />,
      );
      walk(ch);
    });
  })(desc);

  // Boîtes : ascendance colorée (hors racine)…
  ancNodes.forEach((n, i) => {
    if (n.gen === 0) return;
    boxes.push(
      <PersonBox key={`ab${i}`} x={ancX(n)} y={ancY(n)} w={BOX_W} h={BOX_H}
        membre={n.membre} gen={n.gen} style={style} />,
    );
  });
  // …descendance en teinte neutre…
  let db = 0;
  (function walk(n: DescendantNode | null) {
    if (!n) return;
    if (n.gen > 0) {
      boxes.push(
        <PersonBox key={`db${db++}`} x={dxOf.get(n) ?? 0} y={dyOf(n)} w={BOX_W} h={BOX_H}
          membre={n.membre} gen={0} style={style} neutral />,
      );
    }
    n.children.forEach(walk);
  })(desc);
  // …et la racine mise en avant au centre.
  boxes.push(
    <PersonBox key="root" x={midX - BOX_W / 2} y={rootY} w={BOX_W} h={BOX_H}
      membre={rootMembre} gen={0} style={style} emphasize />,
  );

  const bg = resolveBg(style, isDark);
  return (
    <svg ref={ref} width={Math.ceil(W)} height={Math.ceil(H)} viewBox={`0 0 ${Math.ceil(W)} ${Math.ceil(H)}`}
      xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Sablier (ascendance et descendance)">
      <rect x={0} y={0} width={Math.ceil(W)} height={Math.ceil(H)} fill={bg} />
      {edges}
      {boxes}
    </svg>
  );
});

export default SablierChart;
